import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useEffect } from 'react';
import {
  fetchContinuationInit,
  fetchContinuationList,
  fetchContinuationEditInit,
  fetchContinuationAdd,
  fetchR2ContinuationAdd,
  downloadContinuationListPdf,
  deleteContinuation,
  deleteR2Continuation,
  printResetContinuation,
  changePriceContinuation,
  scheduleUpdateContinuation,
  type ContinuationListResponse,
  type ScheduleUpdateVisit,
} from '@/services/main/continuation/continuationService';

export interface ContinuationListParams {
  search_treatment?: string;
  search_treatement_org?: string;
  search_term_type?: number;
  sort?: number;
  [key: string]: unknown;
}

export function useContinuationInit() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const siteType = tenant;

  return useQuery({
    // free_medicals is filtered by X-Service-Type, and switching A2/R2 does not reload the
    // page, so without the service in the key R2 would reuse A2's cached menus.
    queryKey: ['continuation', 'init', siteType, clinicNumber, serviceType],
    queryFn: () => fetchContinuationInit(siteType ?? '', clinicNumber ?? ''),
    enabled: !!siteType && !!clinicNumber,
    staleTime: 60_000,
  });
}

/**
 * `searchNonce` changes on every 検索: BD 3.1 #20 / 4.1 §1.12 say only 検索 recalculates,
 * so the same criteria must not be served from cache (e.g. right after a 「済」 reset).
 */
export function useContinuationList(params: ContinuationListParams | null, searchNonce = 0) {
  const { tenant, clinicNumber } = useClinicContext();
  const siteType = tenant;

  return useQuery({
    queryKey: ['continuation', 'list', siteType, clinicNumber, params, searchNonce],
    queryFn: () => fetchContinuationList(siteType ?? '', clinicNumber ?? '', params ?? {}),
    enabled: !!siteType && !!clinicNumber && params !== null && Object.keys(params).length > 0,
    staleTime: 30_000,
  });
}

export function useContinuationEditInit(patientId: number | undefined, searchYearmonth: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const siteType = tenant;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['continuation', 'edit-init', siteType, clinicNumber, patientId, searchYearmonth],
    queryFn: () =>
      fetchContinuationEditInit(siteType ?? '', clinicNumber ?? '', patientId!, searchYearmonth),
    enabled: !!siteType && !!clinicNumber && !!patientId && !!searchYearmonth,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, refetch };
}

export function useContinuationAdd() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, searchTreatment, sortRadio }: { patientId: number; searchTreatment: string; sortRadio: string }) =>
      fetchContinuationAdd(tenant ?? '', clinicNumber ?? '', patientId, searchTreatment, sortRadio),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['continuation'] });
    },
  });
}

/** R2 counterpart of useContinuationAdd; the R2 list lives under ['visitClinicInvoice']. */
export function useR2ContinuationAdd() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, searchTreatment, sortRadio }: { patientId: number; searchTreatment: string; sortRadio: string }) =>
      fetchR2ContinuationAdd(tenant ?? '', clinicNumber ?? '', patientId, searchTreatment, sortRadio),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitClinicInvoice'] });
    },
  });
}

/** R2 counterpart of useContinuationDelete (r_visits). */
export function useR2ContinuationDelete() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (payload: { delete_id?: number; visit_ids?: number[] }) =>
      deleteR2Continuation(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitClinicInvoice'] });
      toast.success('削除が正常に終了しました。');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

export function useContinuationDelete() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (payload: { delete_id?: number; visit_ids?: number[] }) =>
      deleteContinuation(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['continuation'] });
      toast.success('削除が正常に終了しました。');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

export function useContinuationPrintReset() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (visitId: number) =>
      printResetContinuation(tenant ?? '', clinicNumber ?? '', visitId),
    // BD 3.1 #20 / 4.1 §1.12: reset must NOT recalculate the screen — the amounts stay until
    // 検索 — so only the row's 「済」 is cleared in the cached list instead of refetching it.
    onSuccess: (_data, visitId) => {
      queryClient.setQueriesData<ContinuationListResponse['data']>(
        { queryKey: ['continuation', 'list'] },
        old =>
          old && {
            ...old,
            list: old.list.map(row =>
              row.id === visitId ? { ...row, print_invoice_mark: '' } : row
            ),
          }
      );
      toast.success('プリントチェックをリセットしました。');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

export function useContinuationChangePrice() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({ visitId, freeMedicalFee }: { visitId: number; freeMedicalFee: number }) =>
      changePriceContinuation(tenant ?? '', clinicNumber ?? '', visitId, freeMedicalFee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['continuation'] });
      toast.success('領収金額を変更しました。');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

export function useContinuationScheduleUpdate() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (payload: { patient_id: number; yearmonth: string; visits: ScheduleUpdateVisit[] }) =>
      scheduleUpdateContinuation(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['continuation', 'edit-init', tenant, clinicNumber, variables.patient_id, variables.yearmonth],
      });
      queryClient.invalidateQueries({ queryKey: ['continuation'] });
      toast.success('登録しました。');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });
}

export function useContinuationListPdf(options?: { invalidateOnSuccess?: boolean }) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const invalidateOnSuccess = options?.invalidateOnSuccess ?? true;

  return useMutation({
    mutationFn: (payload: Parameters<typeof downloadContinuationListPdf>[2]) =>
      downloadContinuationListPdf(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      if (invalidateOnSuccess) {
        queryClient.invalidateQueries({ queryKey: ['continuation'] });
      }
    },
  });
}
